import { useTranslation } from 'react-i18next';
import { PHOTO_HOLD_ROLE_LIMITS } from '../photo-hold-roles';
import type { PhotoHoldsController } from '../photo-editor/usePhotoHolds';
import { DockedBar } from './DockedBar';
import { HoldBar, type HoldBarFilter } from './HoldBar';
import type { HoldHandControl } from './HoldHandButton';

/**
 * The bar of hold roles along the bottom of a climb's photo.
 *
 * Always there: with a circle in hand it repaints that circle (several picked
 * up together are repainted or removed as one), and with none it arms the
 * colour the next circle is drawn in. Both are one call on the editor, which
 * is what lets the bar stay put — see `usePhotoHolds#selectRole`.
 *
 * The hand, where hands are forced, is the page's to give (`hand`): the hands
 * follow from the run the page derives, not from anything the editor holds.
 */
export function PhotoHoldBar({
  editor,
  hand,
  filter,
  docked = true,
}: {
  editor: PhotoHoldsController;
  hand?: HoldHandControl;
  filter?: HoldBarFilter;
  /**
   * Over the bottom of the picture (the gym panel's), or the bar alone, for
   * whoever puts it under the picture (the member app's editor, #320).
   */
  docked?: boolean;
}) {
  const { t } = useTranslation();
  const {
    selected,
    anchorRole,
    selectRole,
    roleBlocked,
    deleteSelected,
    clearSelection,
    circuit,
    selectedStep,
    moveSelectedStep,
    canMoveSelectedStep,
  } = editor;

  const bar = (
    <HoldBar
      testId="hold-command-bar"
      role={anchorRole}
      onRole={selectRole}
      // Four starts and two tops, the same numbers a board problem is capped
      // by — a photo has no server-side cap, so this is the whole of the rule.
      unavailable={(role) =>
        roleBlocked(role)
          ? t('climbs.board.roleLimitReached', {
              limit: PHOTO_HOLD_ROLE_LIMITS[role],
              role: t(`climbs.holdRoles.${role}.label`),
            })
          : null
      }
      onDismiss={clearSelection}
      onDelete={deleteSelected}
      canDelete={selected.length > 0}
      deleteLabel={t('climbEditor.holds.delete')}
      hand={hand}
      // Its place in the run, on a circuit — dead while nothing in hand has
      // one: no circle picked up, or a foot, which is no step of the run (see
      // photo-circuit.ts).
      step={
        circuit
          ? {
              order: selectedStep,
              // Up raises the number, which is to say the hold is climbed later.
              onIncrease: () => moveSelectedStep(1),
              onDecrease: () => moveSelectedStep(-1),
              canIncrease: canMoveSelectedStep(1),
              canDecrease: canMoveSelectedStep(-1),
            }
          : undefined
      }
      filter={filter}
    />
  );

  return docked ? <DockedBar>{bar}</DockedBar> : bar;
}
